<template>
    <div class="metrics-chart">
        <div class="metrics-loading" v-if="isLoading"></div>
        <div class="row">
            <div class="col">
                <div class="btn-toolbar">
                    <div v-if="multipleMetricOptions" class="input-group mr-2">
                        <select class="form-control" v-model="metricValue" required name="metric" id="metric" @change="handleSelectChange()">
                            <option v-for="metric in metricOptions" :value="metric">{{ metricOptionLabel(metric) }}</option>
                        </select>
                    </div>
                    <div class="input-group mr-2">
                        <select class="form-control" v-model="periodValue" required name="period" id="period" @change="handleSelectChange()">
                            <option v-for="period in periodOptions" :value="period">{{ periodOptionLabel(period) }}</option>
                        </select>
                    </div>
                    <div class="input-group mr-2">
                        <select class="form-control" v-model="precisionValue" required name="precision" id="precision" @change="handleSelectChange()">
                            <option v-for="precision in precisionOptions" :value="precision">{{ precisionOptionLabel(precision) }}</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col">
                <canvas id="metrics" width="960" height="400" ref="canvas"></canvas>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import { Chart, registerables } from 'chart.js';
import { requestJson } from '../helpers';

Chart.register(...registerables);

const metricOptions = {
    transactions: 'Transações',
    sales_amount: 'Vendas'
};

const periodOptions = {
    hour: 'Última hora',
    day: 'Últimas 24 horas',
    week: 'Última semana',
    month: 'Último mês',
    year: 'Último ano'
};

const precisionOptions = {
    minute: 'Minuto',
    hour: 'Hora',
    day: 'Dia',
    month: 'Mês',
    year: 'Ano'
};

const precisionFormats = {
    'minute': ['YYYY-MM-DD HH:mm:00', 'DD/MM/YYYY HH:mm'],
    'hour': ['YYYY-MM-DD HH:00:00', 'DD/MM/YYYY HH:00'],
    'day': ['YYYY-MM-DD', 'DD/MM/YYYY'],
    'month': ['YYYY-MM', 'MM/YYYY'],
    'year': ['YYYY', 'YYYY']
};

const metricLabelStrings = {
    transactions: 'Valores de transações (R$)',
    sales_amount: 'Valores (R$)'
};

// https://stackoverflow.com/a/56495465
const colors = '#3366cc #dc3912 #ff9900 #109618 #990099 #0099c6 #dd4477 #66aa00 #b82e2e #316395 #994499 #22aa99 #aaaa11 #6633cc #e67300 #8b0707 #651067 #329262 #5574a6 #3b3eac #b77322 #16d620 #b91383 #f4359e #9c5935 #a9c413 #2a778d #668d1c #bea413 #0c5922 #743411'.split(' ');

export default {
    props: {
        isManager: Boolean,
        metricsUrl: String
    },
    data(){
        return {
            metricValue: this.isManager ? 'transactions' : 'sales_amount',
            periodValue: 'month',
            precisionValue: 'day',
            isLoading: false,
            currentChart: null
        };
    },
    computed: {
        multipleMetricOptions(){
            return this.metricOptions.length >= 2;
        },
        metricOptions(){
            const arr = ['sales_amount'];
            if(this.isManager)
                arr.push('transactions');

            return arr;
        },
        periodOptions(){
            return ['hour', 'day', 'week', 'month', 'year'];
        },
        precisionOptions(){
            const arr = [];

            if(['hour'].includes(this.periodValue))
                arr.push('minute');

            if(['day', 'week'].includes(this.periodValue))
                arr.push('hour');

            if(['week', 'month', 'year'].includes(this.periodValue))
                arr.push('day');

            if(['year'].includes(this.periodValue))
                arr.push('month');

            return arr;
        }
    },
    methods: {
        metricOptionLabel(value){
            return metricOptions[value];
        },
        periodOptionLabel(value){
            return periodOptions[value];
        },
        precisionOptionLabel(value){
            return precisionOptions[value];
        },
        async loadMetrics(){
            const shouldUpdate = this.currentAvailability != null;
            const url = this.metricsUrl;
            const params = {
                metric: this.metricValue,
                period: this.periodValue,
                precision: this.precisionValue
            };

            this.isLoading = true;

            try {
                const json = await requestJson(url, params);
                this.displayMetrics(params, json);
            } finally {
                this.isLoading = false;
            }
        },
        displayMetrics(params, data){
            const labelString = metricLabelStrings[params.metric];
            const [intervals, labels] = this.generateIntervals(params.period, params.precision);

            const datasets = data.datasets.map((ds, index) => {
                const amountIndex = ds.data.reduce((carry, { agg_date, amount }) => ({ ...carry, [agg_date]: amount / 100 }), {});
                return {
                    label: ds.label,
                    borderColor: colors[index % colors.length],
                    fill: false,
                    lineTension: 0,
                    borderWidth: 2,
                    data: intervals.map(label => amountIndex[label] || 0)
                };
            });

            const cfg = {
                type: 'line',
                data: { labels, datasets },
                options: {
                    scales: {
                        y: {
                            gridLines: { drawBorder: false },
                            scaleLabel: { display: true, labelString }
                        }
                    }
                }
            };

            if (this.currentChart) {
                this.currentChart.destroy();
            }

            const ctx = this.$refs.canvas.getContext('2d');
            this.currentChart = new Chart(ctx, cfg);
        },
        generateIntervals(period, precision){
            const start = moment().subtract(1, period);
            const [format, labelFormat] = precisionFormats[precision];

            const intervals = [];
            const labels = [];

            for(const max = moment().valueOf(); start.valueOf() <= max; start.add(1, precision)){
                intervals.push(start.format(format));
                labels.push(start.clone().local().format(labelFormat));
            }

            return [intervals, labels];
        },
        handleSelectChange(){
            const opts = this.precisionOptions;
            if(!opts.includes(this.precisionValue)) {
                this.precisionValue = opts[0];
            }

            this.loadMetrics();
        }
    },
    mounted(){
        this.loadMetrics();
    }
}
</script>

<style>
.metrics-chart {
    position: relative;
    margin-top: 2rem;
}

.metrics-loading {
    position: absolute;
    z-index: 1;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background: #fff;
    opacity: .75;
}
</style>
